import { Component, ViewChild, OnInit, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError, map, finalize, switchMap } from 'rxjs/operators';
import {
  EudsDataTableComponent,
  EudsFilterComponent,
  EudsPaginationComponent,
  IMyDateModel
} from '@eucoms/common-components/design-system';
import { DrawerComponent } from '../../components/drawer/drawer.component';
import { environment } from '../../../environments/environment';
import { ApiService, TouchPointFilterParams } from '@app/services/api.service';
import { AppConfigService } from '@eucoms/common-components';
import { AccountTouchPointSummary, InteractionChannelObject } from '@eucoms/customer-touchpoints-tracker';
import { getChannelIcon } from '../../utils/icon-helpers';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['../../app.component.scss', './summary.component.scss']
})
export class SummaryComponent implements OnInit, OnDestroy {
  // Whether this is a production build or not
  isProduction: boolean = environment.production;

  // The OpCo Selected in the base app component
  @Input() opCoSelected: string;
  // The data table
  @ViewChild('tableSummaries') tableSummaries: EudsDataTableComponent;

  // The child pagination component
  @ViewChild('paginationSummaries') paginationSummaries: EudsPaginationComponent;

  // The child drawer component
  @ViewChild('drawer') drawer: DrawerComponent;

  // The child filter component for channels
  @ViewChild('eudsFilterChannel') eudsFilterChannel: EudsFilterComponent;

  // The initial data set (should come from a service)
  allSummariesData: AccountTouchPointSummary[] = [];

  // The data set to be paged
  fetchedSummariesData: AccountTouchPointSummary[] = [];

  // Original data from server (before text search filtering)
  originalSummariesData: any[] = [];

  // The data to be shown on the page
  displayedSummariesData: AccountTouchPointSummary[] = [];

  // Data table column definitions
  columnDefsSummaries = [
    {
      'dataField': 'accountNumber',
      'header': 'Account Number',
      'sortable': true
    },
    {
      'dataField': 'customerInfo.customerNumber',
      'header': 'Customer Number',
      'sortable': true
    },
    {
      'dataField': 'customerInfo.phoneNumber',
      'header': 'Phone Number',
      'sortable': true
    },
    {
      'dataField': 'lastUpdated',
      'header': 'Updated',
      'sortable': true
    },
    {
      'dataField': 'sourceChannel',
      'header': 'Channel',
      'sortable': true
    },
    {
      'dataField': 'summaryText',
      'header': 'Summary',
      'sortable': false
    },
    {
      'dataField': '',
      'header': 'View',
      'sortable': false
    }
  ];

  // The list of options for users to choose how many items per page to show
  itemsPerPageOptions: Array<number> = [25, 50, 100];
  // The chosen number of items per page to show
  itemsPerPage: number = 25;
  // The total number of items in the data set
  totalSummaries: number = 0;
  // The current page displayed
  currentPage: number = 1;

  // Filter field values
  filterAccountNumber: string = '';
  filterCustomerNumber: string = '';
  filterPhoneNumber: string = '';
  filterLastUpdated: string = '1wk'; // Default to filter by last 7 days
  filterChannel: string[] | null = null;
  filterSummary: string = '';

  // Search text filter
  searchText: string = '';
  filterCount: number = 0;
  resultMessageText: string = '';

  // The date ranges to filter the summaries table by
  optionsDateFilter = [
    {
      value: '1wk',
      text: 'Last week'
    },
    {
      value: '2wk',
      text: 'Last 2 weeks'
    },
    {
      value: '1mo',
      text: 'Last month'
    },
    {
      value: 'custom',
      text: 'Custom date range'
    }
  ];

  // Whether to show the date range date picker filter
  showDateRangeFilter: boolean = false;

  // The date picker filter value
  lastUpdatedDateRange: IMyDateModel | null = null;

  // The start and end date filters to use for the API
  filterStartDate: string = '';
  filterEndDate: string = '';

  // The channels to filter the summaries table by
  optionsChannelFilter = Object.values(InteractionChannelObject).map(str => ({ text: str }));

  // Use getChannelIcon function from icon helpers
  getChannelIcon = getChannelIcon;

  // The header text for the slide-in drawer panel
  drawerHeading: string = '';

  // Selected summary to view details for
  selectedSummary: AccountTouchPointSummary | null = null;

  // The summary output to show in the drawer panel
  selectedSummaryOutput: string = '';

  // Whether the view summary panel is open or not
  isViewSummaryDetailsPanelOpen: boolean = false;

  // Whether the json panel is open or not
  isViewJsonDetailsPanelOpen: boolean = false;

  // Whether to show mock data instead
  hasMockData: boolean = false;
  isLoading: boolean = false;
  private routeSubscription: Subscription;

  constructor(
    private appConfig: AppConfigService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.opCoSelected ??= this.appConfig.config.opCo;
    //this.fetchSummariesData();

    this.routeSubscription = this.route.paramMap.subscribe(params => {
      const accountNumberFromRoute = params.get('accountNumber');
      if (accountNumberFromRoute) {
        console.log('Account number from route:', accountNumberFromRoute);
        this.filterAccountNumber = accountNumberFromRoute;
        this.applyFilters();
      } else {
        this.applyFilters();
      }
    });
  }


  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  // Use central time for comed
  get currentTimeZone(): string {
    return this.opCoSelected.toLowerCase() === 'comed' ? 'America/Chicago' : 'America/New_York';
  }

  clickViewSummaryDetails($event: any, summary: AccountTouchPointSummary): void {
    // View session summary details for the selected summary in the drawer panel
    if (this.drawer.drawerOpen) {
      this.drawer.closeDrawer();
    }
    this.selectedSummary = summary;
    this.isViewSummaryDetailsPanelOpen = true;
    this.isViewJsonDetailsPanelOpen = false;
    this.drawerHeading = 'Summary Details';
    this.drawer.openDrawer();
  }

  clickViewJsonDetails($event: any, summary: AccountTouchPointSummary): void {
    // View details (full json) for the selected summary in the drawer panel
    if (this.drawer.drawerOpen) {
      this.drawer.closeDrawer();
    }
    this.selectedSummary = summary;
    this.isViewSummaryDetailsPanelOpen = false;
    this.isViewJsonDetailsPanelOpen = true;
    this.drawerHeading = 'Raw JSON';
    this.selectedSummaryOutput = JSON.stringify(summary, null, '\t');
    this.drawer.openDrawer();
  }

  clickViewTouchpointDetails($event: any, summary: AccountTouchPointSummary): void {
    this.router.navigate([this.appConfig.config.opCo, 'touchpoints', 'account-number', summary.accountNumber]);
  }

  clickViewRealTimePreview($event: any, summary: AccountTouchPointSummary): void {
    this.router.navigate([this.appConfig.config.opCo, 'preview', 'account-number', summary.accountNumber]);
  }

  // Function to safely get nested property value for sorting/filtering
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o && o[key] !== 'undefined') ? o[key] : undefined, obj);
  }

  public sortTableData(event: any) {
    const { field: sortField, order: sortOrder } = event;

    if (sortOrder !== 'none') {
      this.fetchedSummariesData.sort((a: AccountTouchPointSummary, b: AccountTouchPointSummary) => {
        const valueA = this.getNestedValue(a, sortField);
        const valueB = this.getNestedValue(b, sortField);

        // Basic comparison, expand as needed for different types (dates, numbers)
        if (valueA === null || valueA === undefined) return sortOrder === 'ascending' ? -1 : 1;
        if (valueB === null || valueB === undefined) return sortOrder === 'ascending' ? 1 : -1;

        if (valueA > valueB) return sortOrder === 'ascending' ? 1 : -1;
        if (valueA < valueB) return sortOrder === 'ascending' ? -1 : 1;
        return 0;
      });
    } else {
      // If sort order is 'none', potentially reset to default sort or keep current
      // For simplicity, we just update column defs and re-paginate
    }

    this.columnDefsSummaries.forEach((col: any) => {
      col.sortOrder = col.dataField === sortField ? sortOrder : 'none';
    });
    this.showPageItemsForPagination(this.currentPage);
  }

  // Function to set the data shown based on the page chosen
  public onChangePage(page: number = 1): void {
    this.currentPage = page;
    if (this.paginationSummaries) {
      this.showPageItemsForPagination(page);
    }
  }

  filterDataSource(event: any): void {
    this.searchText = event;
    let tableData = [...this.originalSummariesData];

    if (event) {
      const search = this.searchText.toLowerCase();
      tableData = tableData.filter(summary => {
        return (summary.accountNumber as string)?.toLowerCase().includes(search)
          || (summary.customerInfo?.customerNumber as string)?.toLowerCase().includes(search)
          || (summary.customerInfo?.phoneNumber as string)?.toLowerCase().includes(search)
          || (summary.sourceChannel as string)?.toLowerCase().includes(search)
          || (summary.summaryText as string)?.toLowerCase().includes(search);
      });
    }

    this.filterCount = tableData.length;
    this.resultMessageText = `${this.filterCount} summaries found`;

    this.fetchedSummariesData = tableData;
    this.totalSummaries = tableData.length;

    this.currentPage = 1;
    if (this.paginationSummaries) {
      this.paginationSummaries.currentPage = 1;
    }

    this.showPageItemsForPagination(this.currentPage);
  }

  resetDataSource(event: any): void {
    this.searchText = '';

    this.fetchedSummariesData = [...this.originalSummariesData];
    this.totalSummaries = this.originalSummariesData.length;
    this.filterCount = this.totalSummaries;
    this.resultMessageText = `${this.filterCount} summaries found.`;

    this.currentPage = 1;
    if (this.paginationSummaries) {
      this.paginationSummaries.currentPage = 1;
    }

    this.showPageItemsForPagination(this.currentPage);
  }

  // Function to reset the data shown when the items to show per page has been changed
  public onChangeItemsPerPage(items: any): void {
    this.itemsPerPage = items;
    if (this.paginationSummaries) {
      this.showPageItemsForPagination(this.paginationSummaries.currentPage);
    }
  }

  // Function to show a subset of the data based on the current page
  private showPageItemsForPagination(page: number = 1): void {
    const startItem = (page - 1) * this.itemsPerPage;
    const endItem = page * this.itemsPerPage;
    this.totalSummaries = this.fetchedSummariesData.length ?? 0;
    this.displayedSummariesData = this.fetchedSummariesData.slice(startItem, endItem);
  }

  applyFilters(forceRefresh: boolean = false): void {
    this.isLoading = true;
    this.hasMockData = false;
    this.searchText = '';

    // Set date range values (filterStartDate and filterEndDate) based on date filter(s) selected
    if (this.filterLastUpdated !== 'custom') {
      this.filterLastUpdatedValueChanged(this.filterLastUpdated);
    }
    else {
      this.lastUpdatedDateRangeChanged(this.lastUpdatedDateRange);
    }

    const filterParams: TouchPointFilterParams = {
      accountNumber: this.filterAccountNumber || null,
      customerNumber: this.filterCustomerNumber || null,
      phoneNumber: this.filterPhoneNumber || null,
      sourceChannel: this.filterChannel || null,
      startTimeStamp: this.filterStartDate || null,
      endTimeStamp: this.filterEndDate || null
    };

    this.apiService.getSummariesByFilter(filterParams, forceRefresh)
      .pipe(
        map((response: any) => {
          if (response && response.length > 0) {
            this.hasMockData = false;
            return response;
          } else {
            console.warn('No data received from API filter, showing empty list.');
            this.hasMockData = false;
            return [];
          }
        }),
        finalize(() => {
          this.isLoading = false;
          console.log('Filter summaries data complete.');
        }),
        catchError((error) => {
          console.error('Error filtering summaries data: ', error);
          this.hasMockData = false;
          this.isLoading = false;
          return of([]);
        })
      )
      .subscribe((data: AccountTouchPointSummary[]) => {
        // Clientâ€‘side filtering on Summary (all other filters should be server-side)
        const filtered = data.filter(s => {
          if (this.filterSummary && !s.summaryText?.toLowerCase().includes(this.filterSummary.toLowerCase())) return false;
          return true;
        });

        // Reset any sort indicators on every column
        this.columnDefsSummaries.forEach((col: any) => {
          col.sortOrder = 'none';
        });

        // Reâ€‘populate and reâ€‘paginate
        this.fetchedSummariesData = filtered;
        this.originalSummariesData = [...filtered];
        this.totalSummaries = filtered.length;
        this.filterCount = this.totalSummaries;
        this.resultMessageText = `${this.filterCount} summaries found.`;
        this.showPageItemsForPagination(this.currentPage);
      });
  }

  clearFilters(): void {
    // Reset filter model properties
    this.filterAccountNumber = '';
    this.filterCustomerNumber = '';
    this.filterPhoneNumber = '';
    this.filterLastUpdated = '1wk'; // Default to filter by last 7 days
    this.lastUpdatedDateRange = null;
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.eudsFilterChannel.clearAllSelectedOptions();
    this.filterChannel = null;
    this.filterSummary = '';

    // Re-apply filters which will now fetch with empty criteria
    this.applyFilters();
  }

  onOpenDrawer(event: any): void {
    // Drawer opened
  }

  onCloseDrawer(event: any): void {
    // TODO: Reset focus back to original button that opened the drawer ?
    this.isViewSummaryDetailsPanelOpen = false;
    this.isViewJsonDetailsPanelOpen = false;
    this.selectedSummary = null; // Clear selection
  }

  filterLastUpdatedValueChanged(event: any) {
    if (event === 'custom') {
      this.showDateRangeFilter = true;
      this.lastUpdatedDateRange = null;
      // Will update date filters in dateChanged event for date picker instead
      this.setDateRangeValuesForFilter('', '');
    }
    else {
      this.showDateRangeFilter = false;
      let startTimeStamp, endTimeStamp: Date | null;
      // Get a date object for the current date and time
      let d = new Date();
      // Zero the time component
      d.setHours(0, 0, 0, 0);
      switch (event) {
        case '1wk':
          startTimeStamp = new Date(d.setDate(d.getDate() - 7));
          break;
        case '2wk':
          startTimeStamp = new Date(d.setDate(d.getDate() - 14));
          break;
        case '1mo':
          let m = d.getMonth();
          d.setMonth(d.getMonth() - 1);
          // If still in same month, set date to last day of previous month
          if (d.getMonth() == m) d.setDate(0);
          startTimeStamp = new Date(d);
          break;
        default:
          // Default to last 7 days
          startTimeStamp = new Date(d.setDate(d.getDate() - 7));
          break;
      }
      this.setDateRangeValuesForFilter(startTimeStamp.toISOString(), '');
    }
  }

  lastUpdatedDateRangeChanged(event: any) {
    if (this.showDateRangeFilter) {
      if (event?.dateRange?.beginJsDate && event?.dateRange?.endJsDate) {
        let startTimeStamp, endTimeStamp: Date;
        startTimeStamp = event.dateRange.beginJsDate;
        endTimeStamp = new Date(event.dateRange.endJsDate.setHours(23, 59, 59, 999));
        this.setDateRangeValuesForFilter(startTimeStamp.toISOString(), endTimeStamp.toISOString());
      }
      else {
        this.setDateRangeValuesForFilter('', '');
      }
    }
  }

  setDateRangeValuesForFilter(startDate: string, endDate: string) {
    this.filterStartDate = startDate;
    this.filterEndDate = endDate;
  }

  filterChannelSelectedOptionsChanged(selectedOptions: any) {
    if (selectedOptions?.length > 0) {
      // Set filter values for channel
      this.filterChannel = selectedOptions;
    }
    else {
      // For no options, set null filter value for channel
      this.filterChannel = null;
    }
  }

}
